import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视资讯", to: "/news" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isCategoryActive = location.pathname.startsWith("/category");

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/news?q=${encodeURIComponent(value)}` : "/news");
    setMenuOpen(false);
  };

  return (
    <>
      <div className="bg-primary-950 px-4 py-2 text-[11px] text-background-200/80 md:px-6 md:text-xs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2">
            <i className="ri-information-line text-secondary-400" />
            本站为影视资料与资讯站，仅提供文字与图片介绍，不提供在线播放与下载
          </p>
          <p className="flex items-center gap-3">
            <span className="rounded-sm border border-secondary-500/50 px-1.5 py-0.5 text-secondary-400">
              18+
            </span>
            <span className="hidden sm:inline">仅限成年观众浏览</span>
            <Link to="/news" className="cursor-pointer text-background-100 hover:text-secondary-300">
              资讯中心
            </Link>
          </p>
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-20 md:px-6">
          <Link to="/" className="flex cursor-pointer items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 md:h-11 md:w-11">
              <span className="font-heading text-lg font-black text-secondary-200 md:text-xl">三</span>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-black tracking-wide text-foreground-950 md:text-2xl">
                三级片
              </span>
              <span className="mt-1 text-[9px] uppercase tracking-[0.3em] text-foreground-500 md:text-[10px]">
                Category III Archive
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.slice(0, 1).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={
                  location.pathname === item.to
                    ? "cursor-pointer rounded-md bg-primary-100 px-4 py-2 text-sm font-medium text-primary-800"
                    : "cursor-pointer rounded-md px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                }
              >
                {item.label}
              </Link>
            ))}
            <div className="group relative">
              <Link
                to="/category/drama"
                className={
                  isCategoryActive
                    ? "flex cursor-pointer items-center gap-1 rounded-md bg-primary-100 px-4 py-2 text-sm font-medium text-primary-800"
                    : "flex cursor-pointer items-center gap-1 rounded-md px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                }
              >
                影片分类
                <i className="ri-arrow-down-s-line text-base" />
              </Link>
              <div className="invisible absolute left-0 top-full z-50 w-56 pt-2 opacity-0 transition-all group-hover:visible group-hover:opacity-100">
                <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1">
                  {categories.map((category) => (
                    <Link
                      key={category.key}
                      to={`/category/${category.key}`}
                      className="flex cursor-pointer items-center justify-between px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                    >
                      <span className="flex items-center gap-2">
                        <i className="ri-bookmark-3-line text-accent-500" />
                        {category.name}片
                      </span>
                      <span className="text-xs text-foreground-400">{category.en}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {navItems.slice(1).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={
                  location.pathname.startsWith("/news")
                    ? "cursor-pointer rounded-md bg-primary-100 px-4 py-2 text-sm font-medium text-primary-800"
                    : "cursor-pointer rounded-md px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                }
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden items-center lg:flex">
              <div className="flex h-9 items-center overflow-hidden rounded-md border border-background-300 bg-background-50 focus-within:border-primary-400">
                <i className="ri-search-line ml-3 text-sm text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="search"
                  name="q"
                  placeholder="搜索资讯关键词"
                  aria-label="搜索资讯关键词"
                  className="w-44 bg-transparent px-2 py-1 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
                />
                <button
                  type="submit"
                  className="h-full cursor-pointer whitespace-nowrap bg-primary-500 px-3 text-xs text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索
                </button>
              </div>
            </form>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="打开导航菜单"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
            >
              <i className="ri-menu-line text-lg" />
            </button>
            <Link
              to="/news"
              className="hidden cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-3 py-2 text-sm text-primary-700 transition-colors hover:bg-primary-50 md:flex"
            >
              <i className="ri-newspaper-line" />
              资讯中心
            </Link>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="关闭导航菜单"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 h-full w-full cursor-pointer bg-primary-950/50"
          />
          <div className="fade-in absolute right-0 top-0 h-full w-[82%] max-w-xs overflow-y-auto bg-background-50 p-5">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground-950">导航</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="关闭"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={submitSearch} className="mb-5">
              <div className="flex h-10 items-center overflow-hidden rounded-md border border-background-300 bg-background-50">
                <i className="ri-search-line ml-3 text-sm text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="search"
                  name="q"
                  placeholder="搜索资讯关键词"
                  aria-label="搜索资讯关键词"
                  className="min-w-0 flex-1 bg-transparent px-2 py-1 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
                />
                <button
                  type="submit"
                  className="h-full cursor-pointer whitespace-nowrap bg-primary-500 px-3 text-xs text-background-50"
                >
                  搜索
                </button>
              </div>
            </form>

            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="cursor-pointer rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                首页
              </Link>
              <Link
                to="/news"
                onClick={() => setMenuOpen(false)}
                className="cursor-pointer rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                影视资讯
              </Link>
            </div>

            <p className="mb-2 mt-6 px-3 text-xs uppercase tracking-[0.2em] text-foreground-500">
              影片分类
            </p>
            <div className="flex flex-col gap-1">
              {categories.map((category) => (
                <Link
                  key={category.key}
                  to={`/category/${category.key}`}
                  onClick={() => setMenuOpen(false)}
                  className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-bookmark-3-line text-accent-500" />
                    {category.name}片
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
              ))}
            </div>

            <p className="mt-6 rounded-md bg-secondary-100 px-3 py-3 text-xs leading-relaxed text-secondary-900">
              本站仅提供影片资料与资讯整理，不提供任何在线播放或下载入口，内容均为虚构演示。
            </p>
          </div>
        </div>
      )}
    </>
  );
}